/**
 * useOwnership Hook
 * Provides ownership state and actions for entity ownership management
 * Used by content-creators to manage shared access to their content
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import ownershipService, { AccessLevel, hasEditAccess, hasManageAccess } from '../services/ownershipService';

/**
 * @typedef {Object} UseOwnershipOptions
 * @property {boolean} [autoLoad=true] - Load ownership info on mount
 * @property {Function} [onShareSuccess] - Callback when sharing succeeds
 * @property {Function} [onRevokeSuccess] - Callback when revoking succeeds
 * @property {Function} [onError] - Callback on error
 */

/**
 * @typedef {Object} UseOwnershipReturn
 * @property {Object|null} ownershipInfo - Full ownership information
 * @property {string} accessLevel - Current user's access level
 * @property {boolean} canEdit - Whether current user can edit
 * @property {boolean} canDelete - Whether current user can delete
 * @property {boolean} canManageOwnership - Whether current user can manage ownership
 * @property {boolean} isMainOwner - Whether current user is the main owner
 * @property {boolean} isSharedOwner - Whether current user is a shared owner
 * @property {boolean} isAdmin - Whether current user has admin override
 * @property {boolean} loading - Loading state
 * @property {Error|null} error - Error state
 * @property {Function} refresh - Reload ownership info
 * @property {Function} shareWith - Share ownership with a user
 * @property {Function} revoke - Revoke ownership from a user
 * @property {Function} getAvailableUsers - Get users available to share with
 */

/**
 * Hook for managing entity ownership
 * @param {string} entityType - Type of entity (e.g., 'FileEntity', 'Course')
 * @param {number|null} entityId - ID of the entity (null to disable loading)
 * @param {UseOwnershipOptions} [options={}] - Options
 * @returns {UseOwnershipReturn}
 */
export function useOwnership(entityType, entityId, options = {}) {
  const {
    autoLoad = true,
    onShareSuccess,
    onRevokeSuccess,
    onError,
  } = options;

  const { user, hasRole } = useAuth();
  const [ownershipInfo, setOwnershipInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);

  const isMountedRef = useRef(true);

  /**
   * Load ownership information
   */
  const refresh = useCallback(async () => {
    if (!entityType || !entityId) {
      setOwnershipInfo(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const info = await ownershipService.getOwnershipInfo(entityType, entityId);
      if (isMountedRef.current) {
        setOwnershipInfo(info);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        onError?.(err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [entityType, entityId, onError]);

  /**
   * Share ownership with another user
   * @param {string} userId - ID of user to share with
   * @returns {Promise<boolean>} Success status
   */
  const shareWith = useCallback(async (userId) => {
    if (!entityType || !entityId) return false;

    setLoading(true);
    setError(null);

    try {
      await ownershipService.shareOwnership(entityType, entityId, userId);
      await refresh();
      onShareSuccess?.();
      return true;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        onError?.(err);
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [entityType, entityId, refresh, onShareSuccess, onError]);

  /**
   * Revoke ownership from a user
   * @param {string} userId - ID of user to revoke from
   * @returns {Promise<boolean>} Success status
   */
  const revoke = useCallback(async (userId) => {
    if (!entityType || !entityId) return false;

    setLoading(true);
    setError(null);

    try {
      await ownershipService.revokeOwnership(entityType, entityId, userId);
      await refresh();
      onRevokeSuccess?.();
      return true;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        onError?.(err);
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [entityType, entityId, refresh, onRevokeSuccess, onError]);

  /**
   * Get users available to share with
   * @param {string} [search] - Optional search term
   * @returns {Promise<Object[]>} List of available users
   */
  const getAvailableUsers = useCallback(async (search = null) => {
    if (!entityType || !entityId) return [];

    setLoadingAvailableUsers(true);

    try {
      const result = await ownershipService.getAvailableUsersToShare(entityType, entityId, search);
      if (isMountedRef.current) {
        setAvailableUsers(result.users || []);
      }
      return result.users || [];
    } catch (err) {
      if (isMountedRef.current) {
        onError?.(err);
      }
      return [];
    } finally {
      if (isMountedRef.current) {
        setLoadingAvailableUsers(false);
      }
    }
  }, [entityType, entityId, onError]);

  // Auto-load on mount if enabled
  useEffect(() => {
    if (autoLoad && entityType && entityId) {
      refresh();
    }
  }, [autoLoad, entityType, entityId, refresh]);

  // Track mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Compute derived values
  const accessLevel = ownershipInfo?.accessLevel?.toLowerCase() || AccessLevel.NONE;
  const isMainOwner = accessLevel === AccessLevel.MAIN_OWNER;
  const isSharedOwner = accessLevel === AccessLevel.SHARED_OWNER;
  const isAdmin = accessLevel === AccessLevel.ADMIN_OVERRIDE;
  const canEdit = ownershipInfo?.canEdit ?? hasEditAccess(accessLevel);
  const canDelete = ownershipInfo?.canDelete ?? hasEditAccess(accessLevel);
  const canManageOwnership = ownershipInfo?.canManageOwnership ?? hasManageAccess(accessLevel);

  return {
    // State
    ownershipInfo,
    accessLevel,
    canEdit,
    canDelete,
    canManageOwnership,
    isMainOwner,
    isSharedOwner,
    isAdmin,
    loading,
    error,
    availableUsers,
    loadingAvailableUsers,

    // Actions
    refresh,
    shareWith,
    revoke,
    getAvailableUsers,
  };
}

export default useOwnership;
